import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { X, User, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { GoogleButton } from "@/components/GoogleAuth";

export default function AuthModal() {
  const { showAuth, setShowAuth, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [referral, setReferral] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      return (q.get("ref") || localStorage.getItem("rigcalc_ref") || "").toUpperCase();
    } catch {
      return "";
    }
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (!showAuth) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email.trim().toLowerCase(), password);
        toast.success("Signed in");
      } else {
        await register(email.trim().toLowerCase(), password, name || null, referral.trim() || null);
        toast.success(referral ? `Account created — referral applied, +$10 credit on your first paid plan` : "Account created — pick a plan to unlock Pro features");
        localStorage.removeItem("rigcalc_ref");
      }
      setShowAuth(false);
      setPassword("");
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="auth-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={() => setShowAuth(false)}
    >
      <div
        className="w-full max-w-md border border-[#334155] bg-[#11151c]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#334155]">
          <div className="text-xs mono tracking-widest text-[#f59e0b]">
            RIGCALC://{mode === "login" ? "SIGN-IN" : "REGISTER"}
          </div>
          <button
            data-testid="close-auth-btn"
            onClick={() => setShowAuth(false)}
            className="text-[#94a3b8] hover:text-[#f8fafc]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <GoogleButton testid="auth-google-btn" />
          <div className="flex items-center gap-3 text-[10px] mono tracking-widest text-[#64748b]">
            <div className="flex-1 h-px bg-[#334155]" /> OR WITH EMAIL <div className="flex-1 h-px bg-[#334155]" />
          </div>
          {mode === "register" && (
            <label className="block">
              <div className="text-xs mono tracking-wider text-[#94a3b8] uppercase mb-1">Name (optional)</div>
              <div className="flex items-center gap-2 bg-[#0b0e13] border border-[#334155] focus-within:border-[#f59e0b] px-3">
                <User className="w-4 h-4 text-[#64748b]" />
                <input
                  data-testid="auth-name"
                  className="flex-1 bg-transparent py-2 mono text-sm focus:outline-none"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            </label>
          )}
          <label className="block">
            <div className="text-xs mono tracking-wider text-[#94a3b8] uppercase mb-1">Email</div>
            <div className="flex items-center gap-2 bg-[#0b0e13] border border-[#334155] focus-within:border-[#f59e0b] px-3">
              <Mail className="w-4 h-4 text-[#64748b]" />
              <input
                data-testid="auth-email"
                type="email"
                required
                className="flex-1 bg-transparent py-2 mono text-sm focus:outline-none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </label>
          <label className="block">
            <div className="text-xs mono tracking-wider text-[#94a3b8] uppercase mb-1">Password</div>
            <div className="flex items-center gap-2 bg-[#0b0e13] border border-[#334155] focus-within:border-[#f59e0b] px-3">
              <Lock className="w-4 h-4 text-[#64748b]" />
              <input
                data-testid="auth-password"
                type="password"
                required
                minLength={8}
                className="flex-1 bg-transparent py-2 mono text-sm focus:outline-none"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {mode === "register" && (
              <div className="text-[10px] mono text-[#64748b] mt-1">Min 8 characters</div>
            )}
          </label>

          {mode === "register" && (
            <label className="block">
              <div className="text-xs mono tracking-wider text-[#94a3b8] uppercase mb-1">Referral Code (optional)</div>
              <input
                data-testid="auth-referral"
                className="w-full bg-[#0b0e13] border border-[#334155] focus:border-[#f59e0b] px-3 py-2 mono text-sm focus:outline-none uppercase"
                value={referral}
                onChange={(e) => setReferral(e.target.value.toUpperCase())}
                placeholder="8-CHAR CODE"
              />
              {referral && (
                <div className="text-[10px] mono text-[#10b981] mt-1">+$10 credit on your first paid plan</div>
              )}
            </label>
          )}

          {err && (
            <div className="border-l-2 border-[#ef4444] bg-[#ef4444]/10 px-3 py-2 text-xs mono text-[#fca5a5]">
              {err}
            </div>
          )}

          <button
            data-testid="auth-submit"
            type="submit"
            disabled={busy}
            className="w-full bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-4 py-2.5 mono text-xs tracking-widest uppercase font-bold transition-colors disabled:opacity-40"
          >
            {busy ? "..." : mode === "login" ? "Sign in" : "Create account"}
          </button>

          <button
            type="button"
            data-testid="auth-switch"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setErr("");
            }}
            className="w-full text-xs mono text-[#94a3b8] hover:text-[#f59e0b] transition-colors"
          >
            {mode === "login" ? "→ Need an account? Register" : "→ Already have an account? Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
